import { useEffect, useRef } from "react"

import { App, Drawer, Flex, Tooltip, Typography } from "antd"
import { useDispatch, useSelector } from "react-redux"
import { useLocation } from "react-router"
import { useNavigate } from "react-router-dom"

import ButtonComponent from "../../../../Components/Button"
import SelectComponent from "../../../../Components/Select"
import Icon from "../../../../Icon"
import { modalConfig } from "../../../../theme"
import { creditsActions } from "../../redux/credits.slice"
import { getCreditDetails, updateCredit } from "../../redux/credits.thunks"
import { useCreditsPageQuery } from "../../useCreditsPageQuery"
import { buildNotifyPayload, isNotifiableStatusChange } from "../../utils/notify"
import { AttachmentsList } from "../Attachments"
import { NotifyMerchantCheckbox } from "../NotifyMerchantCheckbox"
import { OverviewInfo } from "../OverviewInfo"

const providerStatusOptions = [
  {
    value: "Request sent",
    label: "Request sent"
  },
  {
    value: "Requires proof",
    label: "Requires proof"
  },
  {
    value: "Rejected",
    label: "Rejected"
  },
  {
    value: "Approved",
    label: "Approved"
  },
  {
    value: "Ineligible",
    label: "Ineligible"
  }
]

const { Text } = Typography

export const CreditDrawer = ({ setRejectModalVisible }) => {
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const { modal } = App.useApp()

  const { pathname, search } = useLocation()
  const { openCreditID } = useCreditsPageQuery(search)

  const { isMobile } = useSelector((state) => state.nav)
  const { creditDetails, saveFetching, saveResult } = useSelector((state) => state.credits)
  const { isAdmin } = useSelector((state) => state.auth)

  const notifyRef = useRef(true)

  const open = !!openCreditID && !!creditDetails && isAdmin

  const isDisabledBtn = ["Rejected", "Approved", "Auto approved"].includes(
    creditDetails?.["request_status"]
  )

  const exceededResubmitAttempts = (creditDetails?.enabled_for_resubmit?.resubmit_attempts ?? 0) > 3
  const canReopenRequest = creditDetails?.is_can_reopen_manual_cr
  const isReopenDisabled = !canReopenRequest

  // Tied to the same condition driving `disabled` below (the backend's own
  // is_can_reopen_manual_cr / enabled_for_resubmit.status), not a re-derived day-diff -
  // a separately recomputed "10 days" check could disagree with the backend at the exact
  // boundary, leaving the button disabled with no tooltip explaining why.
  const tooltipText = () => {
    switch (true) {
      case exceededResubmitAttempts:
        return "Maximum resubmit attempts exceeded"
      case isReopenDisabled:
        return "Reopening period (10 days) has expired"
      default:
        return null
    }
  }

  useEffect(() => {
    if (openCreditID) {
      dispatch(getCreditDetails(openCreditID))
    }
  }, [openCreditID, saveResult])

  const handleCloseModal = () => {
    navigate(pathname, { replace: true })
    dispatch(creditsActions.creditsSetProp({ key: "creditDetails", value: null }))
  }

  const handleChangeStatus = (newStatus) => {
    if (newStatus !== creditDetails?.["provider_status"]) {
      const isNotifiable = isNotifiableStatusChange({
        status_type: "provider_status",
        status_value: newStatus
      })

      notifyRef.current = true

      modal.confirm({
        ...modalConfig("info"),
        centered: !isMobile,

        title: "Provider status",
        content: (
          <>
            <Text type="secondary">
              Are you sure you want to change Provider status? Note that changing Provider status
              may affect Request status value and credit balance for corresponding business account.
            </Text>
            {isNotifiable && (
              <div style={{ marginTop: 16 }}>
                <NotifyMerchantCheckbox onChange={(checked) => (notifyRef.current = checked)} />
              </div>
            )}
          </>
        ),
        cancelText: "Go back",
        okText: "Change status",
        cancelButtonProps: { size: "large" },
        okButtonProps: { size: "large" },
        onOk() {
          dispatch(
            updateCredit({
              id: openCreditID,
              status_type: "provider_status",
              status_value: newStatus,
              ...(isNotifiable ? buildNotifyPayload(notifyRef.current) : {})
            })
          )
        }
      })
    }
  }

  const acceptConfirm = () => {
    modal.confirm({
      ...modalConfig("info"),
      centered: !isMobile,

      title: "Approve credit request",
      content: (
        <Text type="secondary">
          Are you sure you want to approve the request so that credits will be added to the business
          account credit balance? This action is irreversible and the credits may not be returned
          back.
        </Text>
      ),
      okText: "Approve",
      cancelButtonProps: { size: "large" },
      okButtonProps: { size: "large" },
      onOk() {
        dispatch(
          updateCredit({
            id: openCreditID,
            status_type: "request_status",
            status_value: "Approved"
          })
        )
      }
    })
  }

  return (
    <Drawer
      title="Request details"
      getContainer={() => document.getElementById("main-layout")}
      width={600}
      placement="right"
      closable={false}
      onClose={handleCloseModal}
      open={!!open}
      destroyOnClose={true}
      styles={{
        content: { borderRadius: "24px" },
        header: { padding: "16px 24px", border: "none" },
        footer: { padding: "8px 24px 24px", border: "none" }
      }}
      rootClassName="drawer-like-modal"
      extra={
        <ButtonComponent
          type="link"
          shape="circle"
          onClick={handleCloseModal}
          icon={<Icon type="cross_small" style={{ color: "#7A7A83" }} />}
        />
      }
      footer={
        <Flex vertical={true} gap={12}>
          <SelectComponent
            size="large"
            name="provider_status"
            placeholder="Set provider status"
            //value={creditDetails?.['provider_status']}
            value={null}
            options={providerStatusOptions?.filter(
              (option) =>
                !(
                  option?.value === creditDetails?.["provider_status"] ||
                  (option?.value === "Ineligible" && creditDetails?.["request_type"] === "System")
                )
            )}
            onChange={handleChangeStatus}
          />

          <Flex gap={12} align="center" justify="space-between">
            {creditDetails?.request_status === "Rejected" &&
              creditDetails?.request_type === "Manual" && (
                <Tooltip title={tooltipText()}>
                  <ButtonComponent
                    size="large"
                    text="Resubmit"
                    onClick={() =>
                      navigate(`${pathname}?open_credit_resubmit=${openCreditID}`, {
                        replace: true
                      })
                    }
                    block
                    loading={!isDisabledBtn && saveFetching}
                    disabled={isReopenDisabled || exceededResubmitAttempts}
                  />
                </Tooltip>
              )}
            {creditDetails?.request_type === "System" &&
              creditDetails?.request_status === "Requires proof" && (
                <ButtonComponent
                  size="large"
                  text="Add evidence"
                  onClick={() => navigate(`?open_credit_request=${openCreditID}&attach=true`)}
                  block
                  loading={!isDisabledBtn && saveFetching}
                />
              )}

            <ButtonComponent
              size="large"
              text="Reject request"
              onClick={() => setRejectModalVisible(true)}
              block
              disabled={isDisabledBtn}
              loading={!isDisabledBtn && saveFetching}
            />

            <ButtonComponent
              size="large"
              type="primary"
              text="Accept request"
              onClick={acceptConfirm}
              block
              disabled={isDisabledBtn}
              loading={!isDisabledBtn && saveFetching}
            />
          </Flex>
        </Flex>
      }
    >
      <Flex vertical={true} gap={12}>
        <OverviewInfo data={creditDetails} type="drawer" />
        <AttachmentsList attachments={creditDetails?.["attachments"] || []}>
          <Text type="secondary" strong>
            Attachments
          </Text>
        </AttachmentsList>
      </Flex>
    </Drawer>
  )
}
